import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { X, Save, Package, AlertCircle } from "lucide-react";

/**
 * StockForm - Edit metadata for an existing inventory item
 *
 * This form is for editing METADATA only:
 * - Expiry date
 * - Supplier
 * - Storage location
 * - Storage requirements
 * - Notes
 *
 * Quantity changes should be done via Stock Transaction to maintain audit trail.
 */
export default function StockForm({ item, onSave, onClose }) {
  const [formData, setFormData] = useState({
    expiryDate: "",
    supplier: "",
    storageLocation: "",
    storageRequirements: "",
    notes: "",
  });

  useEffect(() => {
    if (item) {
      setFormData({
        expiryDate: item.expiryDate
          ? new Date(item.expiryDate).toISOString().split("T")[0]
          : "",
        supplier: item.supplier ?? "",
        storageLocation: item.storageLocation ?? "",
        storageRequirements: item.storageRequirements ?? "",
        notes: item.notes ?? "",
      });
    }
  }, [item]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  if (!item) {
    return null; // This form is only for editing existing items
  }

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-2xl bg-white/90 backdrop-blur-xl border-slate-200/60 shadow-2xl max-h-[90vh] overflow-y-auto">
        <CardHeader className="flex flex-row items-center justify-between sticky top-0 bg-white/80 backdrop-blur-sm z-10 border-b">
          <CardTitle>Edit Stock Item</CardTitle>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Medicine Info (Read-only) */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Package className="w-5 h-5 text-blue-600 mt-0.5" />
                <div className="flex-1">
                  <h3 className="font-semibold text-blue-900">{item.name}</h3>
                  <div className="mt-2 grid grid-cols-2 gap-2 text-sm">
                    <p className="text-blue-700">
                      <span className="text-blue-600">Batch:</span> {item.batchNumber}
                    </p>
                    <p className="text-blue-700">
                      <span className="text-blue-600">Current Stock:</span> {item.currentStock}
                    </p>
                    <p className="text-blue-700">
                      <span className="text-blue-600">Unit Cost:</span> R{Number(item.unitCost || 0).toFixed(2)}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Info about quantity changes */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-amber-800">
                To change stock quantity, use <strong>Stock Transaction</strong> instead.
                This ensures proper audit trail for all stock movements.
              </p>
            </div>

            {/* Expiry Date */}
            <div className="space-y-2">
              <Label htmlFor="expiryDate">Expiry Date *</Label>
              <Input
                id="expiryDate"
                type="date"
                value={formData.expiryDate || ""}
                onChange={(e) => handleInputChange("expiryDate", e.target.value)}
                required
              />
            </div>

            {/* Supplier */}
            <div className="space-y-2">
              <Label htmlFor="supplier">Supplier</Label>
              <Input
                id="supplier"
                value={formData.supplier || ""}
                onChange={(e) => handleInputChange("supplier", e.target.value)}
                placeholder="e.g., ABC Pharmaceuticals"
              />
            </div>

            {/* Storage */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="storageLocation">Storage Location</Label>
                <Input
                  id="storageLocation"
                  value={formData.storageLocation || ""}
                  onChange={(e) => handleInputChange("storageLocation", e.target.value)}
                  placeholder="e.g., Refrigerator A, Shelf 3"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="storageRequirements">Storage Requirements</Label>
                <Input
                  id="storageRequirements"
                  value={formData.storageRequirements || ""}
                  onChange={(e) => handleInputChange("storageRequirements", e.target.value)}
                  placeholder="e.g., Store at 2-8°C"
                />
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes || ""}
                onChange={(e) => handleInputChange("notes", e.target.value)}
                placeholder="Additional notes about this inventory item..."
                rows={3}
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-6 border-t">
              <Button type="button" variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-gradient-to-r from-blue-600 to-indigo-600"
              >
                <Save className="w-4 h-4 mr-2" />
                Update Item
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
